import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { GWDayPickerSingleDateController } from '../../_internal_date_/GWReactDatesWrapper';
// Importing react-dates CSS as per the 'webpack' section of their documentation: https://github.com/react-dates/react-dates?tab=readme-ov-file#webpack
import 'react-dates/lib/css/_datepicker.css';
import { useMediaQuery } from 'react-responsive';
import { DATE_FORMATS } from '../../../utilities/constants';
import { GwDatesWrapper } from '../../_internal_date_/gwDatesWrapper';

import theme from '../themes/theme';
import { CalendarWrapper } from './styles';

const propTypes = {
  /** The date controlling variable. Should be a string */
  date: PropTypes.string,
  /** Callback function called when user selects a date */
  onChange: PropTypes.func.isRequired,
  /** Array of available dates (selectable dates). Format: '2021-12-23T00:00:00' */
  availableDates: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Callback function called when clicked to next month. Contains a string date as param */
  onMonthChange: PropTypes.func,
  /** Bool flag to control the loading state */
  isLoading: PropTypes.bool
};

const defaultProps = {
  date: null,
  onMonthChange: null,
  onPreviousMonthClick: null,
  isLoading: false
};

const shouldDisableButton = currentMonth =>
  GwDatesWrapper.year(currentMonth) <= GwDatesWrapper.year(GwDatesWrapper.today()) &&
  GwDatesWrapper.month(currentMonth) <= GwDatesWrapper.month(GwDatesWrapper.today());

/** Single static datepicker. It has blocked days feature. Note: the available days are being randomly mocked */
const Calendar = ({ date, onChange, availableDates, onMonthChange, isLoading }) => {
  const [focused, setFocused] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(GwDatesWrapper.today());

  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme.breakpoints.lg})`
  });

  const isDateOutsideRage = date => {
    return GwDatesWrapper.isBefore(date, GwDatesWrapper.today(), 'day');
  };

  const isDayBlocked = date => {
    // We don't want past dates to be strikethrough, but they'll be disabled
    if (isDateOutsideRage(date)) {
      return false;
    }

    return !availableDates.find(d => GwDatesWrapper.isSame(date, d, 'day'));
  };

  const showLoading = date => isLoading || GwDatesWrapper.month(date) !== GwDatesWrapper.month(currentMonth);

  const onChangeMonth = date => {
    setCurrentMonth(GwDatesWrapper.format(date, DATE_FORMATS.default));
    onMonthChange(date);
  };

  useEffect(() => {
    if (availableDates.length > 0) {
      setCurrentMonth(availableDates[0]);
    }
  }, [availableDates]);

  return !isLoading ? (
    <CalendarWrapper>
      <GWDayPickerSingleDateController
        date={date}
        onDateChange={onChange}
        focused={focused}
        isLoading={isLoading}
        onFocusChange={() => setFocused(true)}
        numberOfMonths={1}
        hideKeyboardShortcutsPanel
        enableOutsideDays={false}
        daySize={isDesktop ? 60 : 47}
        verticalBorderSpacing={10}
        isOutsideRange={isDateOutsideRage}
        isDayBlocked={isDayBlocked}
        onNextMonthClick={onChangeMonth}
        onPrevMonthClick={onChangeMonth}
        weekDayFormat={'ddd'}
        noNavPrevButton={shouldDisableButton(currentMonth)}
        //Using highlighted feature to control the loading state
        isDayHighlighted={showLoading}
        initialVisibleMonth={() => currentMonth}
      />
    </CalendarWrapper>
  ) : null;
};

Calendar.propTypes = propTypes;
Calendar.defaultProps = defaultProps;

export default Calendar;
